import { useEffect } from 'react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import * as Sentry from '@sentry/react';
import { FullScreenLoading } from '@framework/ui';
import type { AuthStatus } from './authStatus';
import { FirebaseAuthenticationClient } from '../firebase/auth/FirebaseAuthenticationClient';
import type { UserId } from 'balus-common';
import { IdTokenSyncToCookie } from './IdTokenSyncToCookie';
import { ActiveAuthSessionListener } from './ActiveAuthSessionListener';
import { authStateAtom } from './authStateAtom';
import { AuthUnavailableNotice } from './AuthUnavailableNotice';
import { ClaimsFetchError, getClaimsWithRetry } from './getClaimsWithRetry';
import type { ClaimsFailureKind } from './getClaimsWithRetry';
import { buildRegistrationDraft } from './registrationDraft';
import { isEqual } from 'lodash-es';
import { toast } from 'react-hot-toast';

const NullValue: AuthStatus = {
  status: 'unauthenticated',
  currentUserId: null,
  currentSessionId: null,
  userType: null,
  signInAt: null,
};

/**
 * 認証セッションの初回読み取りを待つ上限(ミリ秒)。
 * RTDB に接続できない間 on('value') は発火しないため、打ち切らないとローディング表示のまま固まる。
 */
const SessionSubscriptionTimeoutMilliSeconds = 15_000;

/**
 * promise が指定時間内に解決したら true、時間切れなら false を返す。
 * promise 自体は中断しないため、後から解決すれば購読のコールバックは通常どおり動く。
 */
const resolvesWithin = (promise: Promise<unknown>, milliSeconds: number): Promise<boolean> => {
  let timerId: ReturnType<typeof setTimeout>;
  return Promise.race([
    promise.then(() => true),
    new Promise<boolean>((resolve) => {
      timerId = setTimeout(() => resolve(false), milliSeconds);
    }),
  ]).finally(() => clearTimeout(timerId));
};

type AuthStateFailureCause = `claims-${ClaimsFailureKind}` | 'session-timeout';

/**
 * 認証状態を確定できなかったことを Sentry へ報告する。
 * console.error 経由(captureConsoleIntegration)では原因の切り分けに必要な文脈が残らないため、
 * 明示的に captureException する。
 */
const reportAuthStateFailure = (
  error: unknown,
  cause: AuthStateFailureCause,
  extra: Record<string, unknown> = {}
): void => {
  Sentry.captureException(error, {
    tags: { authStateFailure: cause },
    extra: { ...extra, onLine: navigator.onLine },
  });

  // Sentry は本番ビルドでしか有効でないため、開発時は手掛かりが何も残らない。
  // 本番で captureConsoleIntegration と二重報告にならないよう、開発時のみ出す。
  if (process.env.NODE_ENV !== 'production') console.error(cause, error);
};

type Props = {
  children: (currentUserId: UserId | null) => React.ReactNode;
};

export const AuthStateLoader: React.FC<Props> = ({ children }: Props) => {
  // 認証状態のソース・オブ・トゥルースは Jotai の authStateAtom。
  // 認証状態マシン(下記 onIdTokenChanged の購読)が atom に対して同期的に書き込み、
  // その値を利用して loading ゲート / children を駆動する。
  const { loading, authStatus, authError } = useAtomValue(authStateAtom);
  const setAuthState = useSetAtom(authStateAtom);
  // 購読コールバック内から最新の認証状態を読み取るために store を直接参照する
  const store = useStore();

  // このコンポーネント(AuthStateLoader)の初回描画時に、 Firebase Authentication の認証状態 subscribe を開始する
  useEffect(() => {
    // 発火が短時間に複数回起きた場合、古い発火の非同期処理
    // (getClaims / セッション購読)が後から解決して、
    // 最新の認証結果を上書きする競合が起き得る。発火ごとに世代ID(runId)を採番し、
    // 最新世代でない解決結果は破棄する。
    let latestRunId = 0;

    // latestRunId は本 effect のクロージャ変数のため、cleanup 後に effect が再実行されても
    // 古い世代からは新しい世代の採番が見えず、runId の比較だけでは破棄されない。
    // StrictMode の二重実行やアンマウント後に、解決の遅れた古い世代が現在の atom を
    // 書き換えてしまうため、破棄済みであることを別途持つ。
    let disposed = false;

    // NOTE: onAuthStateChanged ではなく onIdTokenChanged を購読する。
    // signInWithCustomToken で「既にサインイン済みの同一 uid」に再サインインした場合
    // (pendingRegistration からの登録完了フローなど)は onAuthStateChanged が発火せず、
    // onIdTokenChanged のみが発火するため。
    const unsubscribe = FirebaseAuthenticationClient.onIdTokenChanged(async (firebaseUser) => {
      latestRunId += 1;
      const runId = latestRunId;
      // この発火より後に新しい発火があった場合、または本 effect が破棄済みの場合に true を返す。
      const isStale = () => disposed || runId !== latestRunId;

      // 破棄済みの effect からは atom を書き換えない。以降の書き込み箇所と条件を揃える。
      if (isStale()) return;

      if (!firebaseUser) {
        // サインアウト時は、古いセッション購読(RTDB の on('value') / 有効期限タイマー)が
        // 残り続けないよう明示的に停止する。
        ActiveAuthSessionListener.stop();
        setAuthState({ loading: false, authStatus: NullValue, registrationDraft: null, authError: null });
        return;
      }

      getClaimsWithRetry(isStale)
        .then(async (claims) => {
          // getClaims の解決までに新しい発火があれば、本世代の結果は破棄する。
          if (isStale()) return;

          // onIdTokenChanged はトークンの定期リフレッシュ(約1時間ごと)や強制リフレッシュでも
          // 発火する。認証状態が変わらないことが明らかな発火では再評価(loading 化・
          // セッション再購読)を行わず、画面のちらつきと無駄な RTDB アクセスを避ける。
          const { authStatus: currentStatus, registrationDraft: currentDraft } = store.get(authStateAtom);
          const hasSessionClaims = !!(claims && claims.sessionId && claims.userId);
          if (
            hasSessionClaims &&
            currentStatus.status === 'authenticated' &&
            currentStatus.currentSessionId === claims.sessionId &&
            currentStatus.currentUserId === claims.userId
          ) {
            return;
          }
          if (
            !hasSessionClaims &&
            currentStatus.status === 'pendingRegistration' &&
            currentDraft?.firebaseUid === firebaseUser.uid &&
            currentDraft?.emailVerified === firebaseUser.emailVerified
          ) {
            return;
          }

          // 認証状態の再評価を行う間は loading 状態に切り替える。
          // クレイムを取得できた時点で、直前の通信起因のエラー表示は解除してよい。
          setAuthState((prev) => ({ ...prev, loading: true, authError: null }));

          // Balus のセッションが確立できない(クレーム無し / sessionId・userId 欠如)場合は、
          // Firebase 認証済みだが Balus ユーザー未作成 ＝ pendingRegistration とする。
          // クレーム取得自体の失敗(catch)はここには到達しないため、pending には倒さない。
          if (!claims || !claims.sessionId || !claims.userId) {
            const draft = buildRegistrationDraft(firebaseUser);
            const pendingStatus: AuthStatus = { ...NullValue, status: 'pendingRegistration' };
            setAuthState((prev) =>
              isEqual(prev.authStatus, pendingStatus) && isEqual(prev.registrationDraft, draft)
                ? prev
                : { ...prev, authStatus: pendingStatus, registrationDraft: draft }
            );
            return;
          }

          const sessionId = claims.sessionId;

          // 認証セッションの情報をRTDBから取得し、有効期限内であることをチェックする
          const subscribed = await resolvesWithin(
            ActiveAuthSessionListener.start(sessionId, (authSession) => {
              // セッション購読のコールバックは後からも発火し得るため、最新世代でなければ無視する。
              if (isStale()) return;
              if (authSession) {
                const newValue: AuthStatus = {
                  status: 'authenticated',
                  currentSessionId: authSession.sessionId,
                  currentUserId: authSession.userId,
                  userType: authSession.userType,
                  signInAt: authSession.signInAt,
                };
                // AuthStatus に変化がなく登録前情報も既にクリア済みなら更新扱いしない。
                // ログイン確定時は登録前の一時情報(registrationDraft)をクリアする。
                setAuthState((prev) =>
                  isEqual(prev.authStatus, newValue) && prev.registrationDraft === null && prev.authError === null
                    ? prev
                    : { ...prev, authStatus: newValue, registrationDraft: null, authError: null }
                );
              } else {
                // 認証セッションの有効期限が切れているので、フロントエンドの認証状態もサインアウトさせる
                FirebaseAuthenticationClient.signOut();
                setAuthState((prev) => ({
                  ...prev,
                  authStatus: NullValue,
                  registrationDraft: null,
                  authError: null,
                }));
              }
            }),
            SessionSubscriptionTimeoutMilliSeconds
          );

          // RTDB に接続できず初回読み取りが返らない場合も、通信起因として扱う。
          // 購読は生かしたままなので、接続でき次第上記コールバックで認証状態が確定する。
          if (!subscribed && !isStale()) {
            reportAuthStateFailure(
              new Error('Timed out while subscribing to the active auth session'),
              'session-timeout'
            );
            setAuthState({ loading: false, authStatus: NullValue, registrationDraft: null, authError: 'network' });
          }
        })
        .catch((error) => {
          // 古い世代の失敗で最新の認証結果を未ログインに巻き戻さないようガードする。
          if (isStale()) return;

          // 同一ユーザーで既にログイン済みの状態でクレイム取得に失敗した場合は、認証状態を維持する。
          // ここで未ログインに倒すと、通信が一時的に不安定になっただけのユーザーに
          // 再ログインを強いることになるため。トークンが本当に失効している場合は
          // Firebase Authentication 側がサインアウトし、onIdTokenChanged(null) の発火で
          // 未ログインに遷移する。
          //
          // uid の一致を条件にするのは、別ユーザーへのサインイン直後にクレイム取得が失敗した場合に
          // 前のユーザーの認証状態を残さないため(認証セッションの userId は Firebase の uid と同一)。
          const { authStatus: currentStatus } = store.get(authStateAtom);
          const claimsError = error instanceof ClaimsFetchError ? error : null;
          const kind = claimsError?.kind ?? 'unknown';
          // 認証情報そのものが失効している(kind === 'auth')場合だけは維持しない。
          // user-token-expired / user-disabled は Firebase SDK 側がサインアウトするが、
          // invalid-refresh-token などはサインアウトされないため、ここで倒す必要がある。
          const keepsAuthState =
            kind !== 'auth' &&
            currentStatus.status === 'authenticated' &&
            currentStatus.currentUserId === firebaseUser.uid;

          // Sentry へは元の Firebase エラーを渡す。スタックとエラーコードが残り、
          // リトライ結果(kind / attempts)は tag と extra 側で切り分けられるため。
          reportAuthStateFailure(claimsError?.originalError ?? error, `claims-${kind}`, {
            attempts: claimsError?.attempts ?? null,
            keepsAuthState,
          });

          if (keepsAuthState) return;

          // 通信起因の失敗では未ログインに倒さず、再読み込みを促す表示に切り替える。
          // この時点でも Firebase 側にはサインイン済みのユーザーが残っているため、
          // ログイン画面を出すのは誤った案内になる。
          if (kind === 'network') {
            setAuthState({ loading: false, authStatus: NullValue, registrationDraft: null, authError: 'network' });
            return;
          }

          // 認証情報が失効していると判定できた場合は、Firebase 側もサインアウトさせて状態を揃える。
          // invalid-refresh-token のように SDK が自動サインアウトしないコードでは、
          // Jotai だけ未ログインにしても currentUser が残り、再読み込みやトークンイベントの
          // たびに失効したユーザーの評価を繰り返してしまうため。
          // 分類できない(unknown)場合は、後続のトークンイベントで復帰する余地を残して倒すだけにする。
          if (kind === 'auth') {
            FirebaseAuthenticationClient.signOut().catch((signOutError) =>
              reportAuthStateFailure(signOutError, 'claims-auth', { signOutFailed: true })
            );
          }

          // loading のまま固まらないよう未ログインに倒す
          setAuthState({ loading: false, authStatus: NullValue, registrationDraft: null, authError: null });
          toast.error('ログイン処理中にエラーが発生しました');
        })
        .finally(() => {
          // 古い世代の完了で最新世代の loading 状態を誤って解除しないようガードする。
          if (isStale()) return;
          setAuthState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
        });
    });

    return () => {
      disposed = true;
      unsubscribe();
      ActiveAuthSessionListener.stop();

      // NOTE: アンマウント時に atom の状態をリセットしていないので、古い認証状態でレンダリングされる可能性がある。
      // これで問題がある場合には、アンマウント時に明示的に atom の認証状態をクリアする必要がある。
      // (破棄後に古い世代が atom を書き換えることは disposed で防いでいる)
    };
  }, [setAuthState, store]);

  return (
    <div className="h-full">
      {loading ? (
        <FullScreenLoading />
      ) : authError === 'network' ? (
        <AuthUnavailableNotice />
      ) : (
        <>
          {children(authStatus.currentUserId)}

          {/* ログイン中ユーザのIDトークンをCookieに同期する */}
          <IdTokenSyncToCookie authStatus={authStatus} />
        </>
      )}
    </div>
  );
};
